import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BarChart3, ArrowLeft, Loader2, ChevronDown, ChevronRight, Award, CheckCircle2, Clock, Circle, Download } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { useI18n } from "../../context/I18nContext";
import { api } from "../../lib/api";

const ease = [0.16, 1, 0.3, 1];
const STATUS_ICON = { completed: CheckCircle2, in_progress: Clock, not_started: Circle };
const STATUS_CLS = { completed: "bg-emerald-500/10 text-emerald-600", in_progress: "bg-amber-500/10 text-amber-600", not_started: "bg-navy/5 text-navy/40" };

function Stat({ label, value, sub, tone = "text-navy" }) {
  return (
    <div className="bg-white border border-navy/10 rounded-lg p-5">
      <div className="overline text-navy/40">{label}</div>
      <div className={`text-3xl font-medium mt-1 ${tone}`}>{value}</div>
      {sub && <div className="text-xs text-navy/40 mt-1">{sub}</div>}
    </div>
  );
}

export default function AcademyDashboard() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(() => {
    api.get("/academy/dashboard").then((r) => setData(r.data)).catch(() => toast.error(t("os.academy.dashLoadErr")));
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const download = async (certId) => {
    try {
      const r = await api.get(`/academy/certificates/${certId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = "certificado-omnia.pdf"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(t("os.academy.certDownloadErr")); }
  };

  if (!data) return <OSLayout title={t("os.academy.trackTitle")}><div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-violet" /></div></OSLayout>;

  const { stats, users, courses } = data;

  return (
    <OSLayout title={t("os.academy.trackTitle")}>
      <div className="space-y-6">
        <Link to="/os/academia" data-testid="dash-back" className="inline-flex items-center gap-2 text-sm text-navy/50 hover:text-navy"><ArrowLeft className="w-4 h-4" /> {t("os.academy.backToAcademy")}</Link>
        <div className="flex items-center gap-2 text-violet"><BarChart3 className="w-5 h-5" /><span className="overline text-navy/40">{t("os.academy.trackSubtitle")}</span></div>
        <h1 className="text-3xl font-medium tracking-tight -mt-2">{t("os.academy.teamProgress")}</h1>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="dash-stats">
          <Stat label={t("os.academy.courses")} value={stats.courses} />
          <Stat label={t("os.academy.users")} value={stats.users} />
          <Stat label={t("os.academy.completion")} value={`${stats.completion_rate}%`} sub={`${stats.completed}/${stats.assigned} ${t("os.academy.assignments")}`} tone="text-violet" />
          <Stat label={t("os.academy.average")} value={stats.avg_score != null ? `${stats.avg_score}%` : "—"} sub={t("os.academy.bestScores")} />
          <Stat label={t("os.academy.certificates")} value={stats.certificates} tone="text-gold" />
        </div>

        {/* Por curso */}
        <div className="bg-white border border-navy/10 rounded-lg overflow-hidden" data-testid="dash-courses">
          <div className="px-5 py-3.5 border-b border-navy/5 overline text-navy/40">{t("os.academy.byCourse")}</div>
          {courses.length === 0 ? <p className="p-6 text-sm text-navy/40">{t("os.academy.emptyUser")}</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-navy/40 text-xs border-b border-navy/5">
                  <th className="px-5 py-2.5 font-medium">{t("os.academy.course")}</th>
                  <th className="px-3 py-2.5 font-medium">{t("os.academy.eligible")}</th>
                  <th className="px-3 py-2.5 font-medium">{t("os.academy.completedCol")}</th>
                  <th className="px-3 py-2.5 font-medium">{t("os.academy.inProgressCol")}</th>
                  <th className="px-3 py-2.5 font-medium">{t("os.academy.notStartedCol")}</th>
                  <th className="px-3 py-2.5 font-medium">{t("os.academy.average")}</th>
                  <th className="px-5 py-2.5 font-medium w-40">{t("os.academy.completionPct")}</th>
                </tr></thead>
                <tbody>
                  {courses.map((c) => (
                    <tr key={c.id} className="border-b border-navy/5 last:border-0" data-testid={`dash-course-${c.id}`}>
                      <td className="px-5 py-3 font-medium">{c.title}</td>
                      <td className="px-3 py-3 text-navy/60">{c.eligible}</td>
                      <td className="px-3 py-3 text-emerald-600">{c.completed}</td>
                      <td className="px-3 py-3 text-amber-600">{c.in_progress}</td>
                      <td className="px-3 py-3 text-navy/40">{c.not_started}</td>
                      <td className="px-3 py-3">{c.avg_score != null ? `${c.avg_score}%` : "—"}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 rounded-full bg-navy/10 overflow-hidden"><div className="h-full bg-violet" style={{ width: `${c.completion_rate}%` }} /></div>
                          <span className="text-xs text-navy/50 w-9 text-right">{c.completion_rate}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Por usuario */}
        <div className="bg-white border border-navy/10 rounded-lg overflow-hidden" data-testid="dash-users">
          <div className="px-5 py-3.5 border-b border-navy/5 overline text-navy/40">{t("os.academy.byUser")}</div>
          {users.length === 0 ? <p className="p-6 text-sm text-navy/40">{t("os.academy.noUsers")}</p> : (
            <div className="divide-y divide-navy/5">
              {users.map((u) => {
                const open = expanded[u.id];
                return (
                  <div key={u.id} data-testid={`dash-user-${u.id}`}>
                    <button onClick={() => setExpanded((e) => ({ ...e, [u.id]: !e[u.id] }))} data-testid={`dash-user-toggle-${u.id}`}
                      className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-navy/[0.02] transition-colors">
                      {open ? <ChevronDown className="w-4 h-4 text-navy/30 shrink-0" /> : <ChevronRight className="w-4 h-4 text-navy/30 shrink-0" />}
                      <div className="w-8 h-8 rounded-full bg-gradient-violet flex items-center justify-center text-white text-[11px] shrink-0">{(u.name || "U").slice(0, 2).toUpperCase()}</div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{u.name} <span className="text-xs text-navy/40 font-normal">· {t(`os.academy.roles.${u.role}`) || u.role}</span></div>
                        <div className="text-xs text-navy/40">{u.completed} {t("os.academy.completedLc")} · {u.in_progress} {t("os.academy.inProgressLc")} · {u.not_started} {t("os.academy.pendingLc")}</div>
                      </div>
                      <div className="hidden sm:flex items-center gap-4 shrink-0 text-xs">
                        <span className="text-navy/40">{t("os.academy.average")}</span>
                        <span className="text-lg font-medium text-navy">{u.avg_score != null ? `${u.avg_score}%` : "—"}</span>
                      </div>
                    </button>
                    {open && (
                      <div className="px-5 pb-4 pl-16 space-y-1.5">
                        {u.courses.length === 0 ? <p className="text-xs text-navy/40 py-2">{t("os.academy.noCoursesAssigned")}</p> : u.courses.map((c) => {
                          const Icon = STATUS_ICON[c.status]; const cls = STATUS_CLS[c.status]; const lbl = t(`os.academy.dashStatus.${c.status}`);
                          return (
                            <div key={c.course_id} className="flex items-center gap-3 text-sm py-1.5">
                              <span className={`text-[11px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 shrink-0 ${cls}`}><Icon className="w-3 h-3" /> {lbl}</span>
                              <span className="flex-1 min-w-0 truncate">{c.title}</span>
                              {c.best_score != null && <span className="text-navy/60 shrink-0">{c.best_score}%</span>}
                              {c.attempts > 0 && <span className="text-navy/30 text-xs shrink-0">· {c.attempts} {c.attempts > 1 ? t("os.academy.attempts") : t("os.academy.attempt")}</span>}
                              {c.certificate_id && <button title={t("os.academy.downloadCert")} onClick={() => download(c.certificate_id)} className="text-gold hover:opacity-70 shrink-0"><Download className="w-4 h-4" /></button>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </OSLayout>
  );
}
